import React, { useState, useMemo } from 'react';
import { Calculator, DollarSign, TrendingUp, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

interface LoanCalculatorDemoProps {
  totalPropertyValue: number;
  goingConcernNOI?: number;
  businessValue?: number;
  ffeValue?: number;
}

const LoanCalculatorDemo: React.FC<LoanCalculatorDemoProps> = ({
  totalPropertyValue = 2800000,
  goingConcernNOI = 420000, // Default to ~15% cap rate
  businessValue = 0,
  ffeValue = 0
}) => {
  // Toggle states
  const [noiBasis, setNoiBasis] = useState<'real-estate' | 'going-concern'>('going-concern');
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Basic inputs
  const [rate, setRate] = useState<number>(7.5);
  const [amortization, setAmortization] = useState<number>(20);
  const [loanTerm, setLoanTerm] = useState<number>(5);
  const [targetDSCR, setTargetDSCR] = useState<number>(1.35);
  const [targetDebtYield, setTargetDebtYield] = useState<number>(15);
  const [ltvCap, setLtvCap] = useState<number>(65);
  
  // Advanced inputs
  const [interestOnlyMonths, setInterestOnlyMonths] = useState<number>(12);
  const [rateStress, setRateStress] = useState<number>(200); // basis points
  const [globalDSCR, setGlobalDSCR] = useState<boolean>(false);
  const [capexReserve, setCapexReserve] = useState<number>(0);

  // Derived values based on NOI basis
  const currentNOI = useMemo(() => {
    // NOI stays the same regardless of basis - it's the value that changes
    return goingConcernNOI - capexReserve;
  }, [goingConcernNOI, capexReserve]);

  const currentValue = useMemo(() => {
    // Real Estate Only = Going Concern - Business Value - FF&E
    return noiBasis === 'real-estate'
      ? totalPropertyValue - businessValue - ffeValue
      : totalPropertyValue;
  }, [noiBasis, totalPropertyValue, businessValue, ffeValue]);

  // Mortgage constant calculation
  const mortgageConstant = useMemo(() => {
    const monthlyRate = rate / 100 / 12;
    const totalPayments = amortization * 12;
    if (monthlyRate === 0) return 1 / amortization; // If 0% rate
    
    const monthlyConstant = monthlyRate * Math.pow(1 + monthlyRate, totalPayments) / 
                           (Math.pow(1 + monthlyRate, totalPayments) - 1);
    return monthlyConstant * 12; // Annual constant
  }, [rate, amortization]);

  // Loan sizing calculations
  const loanSizing = useMemo(() => {
    // DSCR-limited loan
    const dscrLoan = currentNOI / (targetDSCR * mortgageConstant);
    
    // Debt Yield-limited loan
    const debtYieldLoan = currentNOI / (targetDebtYield / 100);
    
    // LTV-limited loan
    const ltvLoan = currentValue * (ltvCap / 100);
    
    // Available loan (minimum of the three)
    const availableLoan = Math.min(dscrLoan, debtYieldLoan, ltvLoan);
    
    // Determine binding constraint
    let bindingConstraint = '';
    if (availableLoan === dscrLoan) bindingConstraint = 'DSCR';
    else if (availableLoan === debtYieldLoan) bindingConstraint = 'Debt Yield';
    else bindingConstraint = 'LTV';
    
    // Calculate slack to non-binding constraints
    const slackToDSCR = dscrLoan - availableLoan;
    const slackToDebtYield = debtYieldLoan - availableLoan;
    const slackToLTV = ltvLoan - availableLoan;
    
    return {
      dscrLoan,
      debtYieldLoan,
      ltvLoan,
      availableLoan,
      bindingConstraint,
      slackToDSCR,
      slackToDebtYield,
      slackToLTV
    };
  }, [currentNOI, currentValue, targetDSCR, targetDebtYield, ltvCap, mortgageConstant]);

  // Health checks
  const healthChecks = useMemo(() => {
    const annualDebtService = loanSizing.availableLoan * mortgageConstant;
    const interestOnlyService = loanSizing.availableLoan * (rate / 100);
    
    // Year 1 DSCR (with IO if applicable)
    const year1DSCR = interestOnlyMonths > 0 ? 
      currentNOI / interestOnlyService : 
      currentNOI / annualDebtService;
    
    // Refi snapshot at term
    const remainingBalance = loanSizing.availableLoan * Math.pow(1 + rate/100/12, loanTerm * 12) - 
      (annualDebtService / 12) * ((Math.pow(1 + rate/100/12, loanTerm * 12) - 1) / (rate/100/12));
    
    const stressedRate = rate + (rateStress / 100);
    const remainingAmort = amortization - loanTerm;
    const stressedMonthlyRate = stressedRate / 100 / 12;
    const stressedPayment = remainingBalance * stressedMonthlyRate * Math.pow(1 + stressedMonthlyRate, remainingAmort * 12) / 
                           (Math.pow(1 + stressedMonthlyRate, remainingAmort * 12) - 1);
    const refiDSCR = currentNOI / (stressedPayment * 12);
    
    return {
      mortgageConstant: mortgageConstant * 100, // Convert to percentage
      year1DSCR,
      remainingBalance,
      refiDSCR,
      annualDebtService
    };
  }, [loanSizing.availableLoan, mortgageConstant, rate, currentNOI, interestOnlyMonths, loanTerm, amortization, rateStress]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number, decimals = 2) => {
    return `${value.toFixed(decimals)}%`;
  };

  const getRefiColor = (dscr: number) => {
    if (dscr >= 1.25) return 'text-green-600 bg-green-50 border-green-200';
    if (dscr >= 1.10) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-green-100 rounded-lg">
          <Calculator className="h-6 w-6 text-green-600" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">What Can I Borrow?</h3>
          <p className="text-sm text-gray-600">Interactive loan sizing calculator</p>
        </div>
      </div>

      {/* NOI Basis Toggle */}
      <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
        <span className="text-sm font-medium text-gray-700">NOI Basis:</span>
        <div className="flex gap-2">
          <button
            onClick={() => setNoiBasis('real-estate')}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              noiBasis === 'real-estate'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Real Estate Only
          </button>
          <button
            onClick={() => setNoiBasis('going-concern')}
            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
              noiBasis === 'going-concern'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Going Concern
          </button>
        </div>
        <div className="ml-auto text-sm text-gray-600">
          NOI: {formatCurrency(currentNOI)} | Value: {formatCurrency(currentValue)}
        </div>
      </div>

      {/* Basic Controls */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Rate (%)</label>
          <input
            type="number"
            value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            step="0.25"
            min="0"
            max="20"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Amortization (yrs)</label>
          <input
            type="number"
            value={amortization}
            onChange={(e) => setAmortization(Number(e.target.value))}
            min="5"
            max="30"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Loan Term (yrs)</label>
          <input
            type="number"
            value={loanTerm}
            onChange={(e) => setLoanTerm(Number(e.target.value))}
            min="1"
            max="30"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">DSCR Target</label>
          <input
            type="number"
            value={targetDSCR}
            onChange={(e) => setTargetDSCR(Number(e.target.value))}
            step="0.05"
            min="1.0"
            max="2.0"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Debt Yield (%)</label>
          <input
            type="number"
            value={targetDebtYield}
            onChange={(e) => setTargetDebtYield(Number(e.target.value))}
            step="0.5"
            min="5"
            max="20"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">LTV Cap (%)</label>
          <input
            type="number"
            value={ltvCap}
            onChange={(e) => setLtvCap(Number(e.target.value))}
            step="5"
            min="30"
            max="90"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>
      </div>

      {/* Advanced Controls Toggle */}
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
      >
        {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        Advanced Options
      </button>

      {showAdvanced && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Interest-Only (months)</label>
            <select
              value={interestOnlyMonths}
              onChange={(e) => setInterestOnlyMonths(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value={0}>0</option>
              <option value={6}>6</option>
              <option value={12}>12</option>
              <option value={24}>24</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rate Stress (bps)</label>
            <input
              type="number"
              value={rateStress}
              onChange={(e) => setRateStress(Number(e.target.value))}
              step="25"
              min="0"
              max="500"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">CapEx Reserve</label>
            <input
              type="number"
              value={capexReserve}
              onChange={(e) => setCapexReserve(Number(e.target.value))}
              step="1000"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>
      )}

      {/* Loan Sizing Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Loan Sizing Summary */}
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Loan Sizing Summary</h4>
          <div className="space-y-3">
            {/* DSCR-Limited */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">DSCR-Limited</span>
                {loanSizing.bindingConstraint === 'DSCR' && (
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">Binding</span>
                )}
              </div>
              <div className="text-right">
                <div className="font-bold">{formatCurrency(loanSizing.dscrLoan)}</div>
                {loanSizing.bindingConstraint !== 'DSCR' && (
                  <div className="text-xs text-green-600">+{formatCurrency(loanSizing.slackToDSCR)} available</div>
                )}
              </div>
            </div>

            {/* Debt Yield-Limited */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Debt Yield-Limited</span>
                {loanSizing.bindingConstraint === 'Debt Yield' && (
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">Binding</span>
                )}
              </div>
              <div className="text-right">
                <div className="font-bold">{formatCurrency(loanSizing.debtYieldLoan)}</div>
                {loanSizing.bindingConstraint !== 'Debt Yield' && (
                  <div className="text-xs text-green-600">+{formatCurrency(loanSizing.slackToDebtYield)} available</div>
                )}
              </div>
            </div>

            {/* LTV-Limited */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">LTV-Limited</span>
                {loanSizing.bindingConstraint === 'LTV' && (
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">Binding</span>
                )}
              </div>
              <div className="text-right">
                <div className="font-bold">{formatCurrency(loanSizing.ltvLoan)}</div>
                {loanSizing.bindingConstraint !== 'LTV' && (
                  <div className="text-xs text-green-600">+{formatCurrency(loanSizing.slackToLTV)} available</div>
                )}
              </div>
            </div>

            {/* Available Loan */}
            <div className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-blue-900">Available Loan</span>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-900">{formatCurrency(loanSizing.availableLoan)}</div>
                <div className="text-sm text-blue-700">{loanSizing.bindingConstraint}-limited</div>
              </div>
            </div>
          </div>
        </div>

        {/* Health & Checks */}
        <div>
          <h4 className="text-lg font-semibold text-gray-900 mb-4">Health & Checks</h4>
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-700">Mortgage Constant</div>
              <div className="text-xl font-bold">{formatPercent(healthChecks.mortgageConstant)}</div>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-700">Year 1 DSCR</div>
              <div className="text-xl font-bold">{healthChecks.year1DSCR.toFixed(2)}×</div>
              <div className="text-xs text-gray-600">
                {interestOnlyMonths > 0 ? `Interest-only (${interestOnlyMonths} months)` : 'Amortizing'}
              </div>
            </div>

            <div className="p-3 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-700">Annual Debt Service</div>
              <div className="text-xl font-bold">{formatCurrency(healthChecks.annualDebtService)}</div>
            </div>

            <div className={`p-3 rounded-lg border ${getRefiColor(healthChecks.refiDSCR)}`}>
              <div className="text-sm font-medium">Refi Snapshot (Year {loanTerm})</div>
              <div className="text-lg font-bold">{healthChecks.refiDSCR.toFixed(2)}× DSCR</div>
              <div className="text-xs">
                Balance: {formatCurrency(healthChecks.remainingBalance)} | Rate: {formatPercent(rate + rateStress/100)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="font-medium text-yellow-800 mb-1">Important Disclaimer</h5>
            <p className="text-sm text-yellow-700">
              These are illustrative underwriting gates, not credit advice. Actual loan terms may vary based on 
              borrower qualifications, property specifics, market conditions, and lender requirements. 
              Consult with qualified commercial lenders for personalized financing guidance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanCalculatorDemo;
